// Beat 4 combined visual: doc quote image + mad-lib card. Image fills the
// wrapper (aspect-fit, object-bottom). Mad-lib is absolutely positioned inside
// and its top is animated between the "reading" offset (clears hero-1) and
// the image foot — so the drop lands exactly on the image's bottom edge on
// every viewport without cross-fading between two separate segments.
//
// The wrapper locks the image's aspect ratio, with max-h and max-w only as upper
// bounds. Separate max-w and max-h ladders on the image and card drift apart at
// sizes that hit one cap before the other.

import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';

import { ASSETS } from '@airhead-games/shared/configs';
import BeatFourMadLibCard from './BeatFourMadLibCard';

type BeatFourVisualGroupProps = {
  scrollVh: MotionValue<number>;
};

const DROP_START_VH = 222;
const DROP_END_VH = 225;

const IMAGE_FADE_IN_START_VH = 220;
const IMAGE_FADE_IN_END_VH = 224.8;
const IMAGE_FADE_OUT_START_VH = 247.5;
const IMAGE_FADE_OUT_END_VH = 250;
const IMAGE_SLIDE_IN_PX = 340;

/** Pixel offset added to the mad-lib's dropped position so the card sits
 *  slightly below the image's foot rather than flush on it. */
const DROP_NUDGE_PX = 16;

/** Per-breakpoint rem offset from the wrapper top where mad-lib sits during
 *  the "reading" phase. Chosen to clear hero-1's bottom with a small buffer
 *  so there's breathing room between the hero copy and the mad-lib card. */
const computeReadingOffsetRem = (width: number): number => {
  if (width >= 1280) return 10.5; // xl, 2xl
  if (width >= 1024) return 8.5; // lg
  if (width >= 768) return 7.5; // md
  if (width >= 640) return 3.5; // sm
  return 3; // mobile
};

const getRootFontSizePx = (): number => {
  if (typeof document === 'undefined') return 16;
  const parsed = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
};

const computeReadingOffsetPx = (): number => {
  if (typeof window === 'undefined') return 32;
  return computeReadingOffsetRem(window.innerWidth) * getRootFontSizePx();
};

const useElementHeight = (ref: React.RefObject<HTMLElement | null>): number => {
  const [height, setHeight] = React.useState(0);
  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    setHeight(node.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => {
      setHeight(entries[0].contentRect.height);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return height;
};

const BeatFourVisualGroup: React.FC<BeatFourVisualGroupProps> = ({
  scrollVh,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const wrapperH = useElementHeight(wrapperRef);
  const cardH = useElementHeight(cardRef);
  const [imageFailed, setImageFailed] = React.useState(false);
  const [readingOffsetPx, setReadingOffsetPx] = React.useState(() =>
    computeReadingOffsetPx()
  );

  React.useEffect(() => {
    const update = () => setReadingOffsetPx(computeReadingOffsetPx());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const madLibTop = useTransform(scrollVh, (v) => {
    const droppedTop = Math.max(0, wrapperH - cardH + DROP_NUDGE_PX);
    if (v <= DROP_START_VH) return readingOffsetPx;
    if (v >= DROP_END_VH) return droppedTop;
    const t = (v - DROP_START_VH) / (DROP_END_VH - DROP_START_VH);
    return readingOffsetPx + (droppedTop - readingOffsetPx) * t;
  });

  const imageOpacity = useTransform(scrollVh, (v) => {
    if (v <= IMAGE_FADE_IN_START_VH) return 0;
    if (v < IMAGE_FADE_IN_END_VH) {
      return (
        (v - IMAGE_FADE_IN_START_VH) /
        (IMAGE_FADE_IN_END_VH - IMAGE_FADE_IN_START_VH)
      );
    }
    if (v <= IMAGE_FADE_OUT_START_VH) return 1;
    if (v < IMAGE_FADE_OUT_END_VH) {
      return (
        1 -
        (v - IMAGE_FADE_OUT_START_VH) /
          (IMAGE_FADE_OUT_END_VH - IMAGE_FADE_OUT_START_VH)
      );
    }
    return 0;
  });

  const imageY = useTransform(scrollVh, (v) => {
    if (v <= IMAGE_FADE_IN_START_VH) return IMAGE_SLIDE_IN_PX;
    if (v < IMAGE_FADE_IN_END_VH) {
      const t =
        (v - IMAGE_FADE_IN_START_VH) /
        (IMAGE_FADE_IN_END_VH - IMAGE_FADE_IN_START_VH);
      return IMAGE_SLIDE_IN_PX * (1 - t);
    }
    if (v <= IMAGE_FADE_OUT_START_VH) return 0;
    if (v < IMAGE_FADE_OUT_END_VH) {
      const t =
        (v - IMAGE_FADE_OUT_START_VH) /
        (IMAGE_FADE_OUT_END_VH - IMAGE_FADE_OUT_START_VH);
      return IMAGE_SLIDE_IN_PX * t;
    }
    return IMAGE_SLIDE_IN_PX;
  });

  return (
    <div
      ref={wrapperRef}
      // The doc-meme PNG is 1256×1466. Locking the wrapper to that exact
      // aspect ratio couples the image and the mad-lib card to the same
      // bounding box: the image fills the wrapper (no whitespace), and the
      // card — which is `absolute left-0 right-0` inside this wrapper —
      // tracks the image's rendered width on every viewport. This is what
      // keeps the "meme template" and the "meme image" visually
      // proportional instead of the card sprawling past the image.
      //
      // Max-width / max-height caps still apply as upper bounds; browser
      // picks the most restrictive and shrinks the wrapper to match while
      // preserving the aspect ratio.
      className={[
        'relative mx-auto h-full w-full aspect-[1256/1466]',
        'max-h-[28rem] max-w-[28rem]',
        'sm:max-h-[40rem] sm:max-w-xl',
        'md:max-h-[48rem] md:max-w-2xl',
        'lg:max-h-[55rem] lg:max-w-3xl',
        'xl:max-h-[62rem] xl:max-w-4xl',
        '2xl:max-h-[72rem] 2xl:max-w-5xl',
        'min-[1920px]:max-h-[80rem] min-[1920px]:max-w-6xl',
      ].join(' ')}
    >
      {imageFailed ? (
        <motion.div
          style={{ opacity: imageOpacity, y: imageY }}
          className="block h-full w-full rounded-xl bg-muted"
          aria-hidden
        />
      ) : (
        <motion.img
          src={ASSETS.docHomeMeme}
          alt=""
          style={{ opacity: imageOpacity, y: imageY }}
          className="block h-full w-full rounded-xl object-contain object-bottom"
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      )}
      <motion.div
        ref={cardRef}
        className="absolute left-0 right-0 flex justify-center"
        style={{ top: madLibTop }}
      >
        <BeatFourMadLibCard scrollVh={scrollVh} startVh={185} durationVh={95} />
      </motion.div>
    </div>
  );
};

export default BeatFourVisualGroup;
